import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-2 — useFinanceExtrato.
//
// Estado total:
//   filters: { q, accountIds[], category, paymentMethods[], period,
//              type, startDate, endDate }
//   page:    offset interno (pula em fetchMore)
//   data:    { transactions[], totalCount, summary, hasMore }
//
// URL sync: 8 chaves (q, accounts, category, payments, period, type,
// from, to). Filtros vão pra URL com debounce — search 300ms, dropdowns
// imediato. fetchMore NÃO altera URL (deep link sempre carrega página 1).
//
// Refetch automático sempre que qualquer filtro muda. Resetа transactions
// e offset=0.
//
// fetchMore appenda no array (next page).
//
// exportCsv abre download via window.location (preserva headers Bearer
// porque rota /export é GET; usa fetch+blob+a click pra honrar Auth header).

const DEFAULTS = {
  q: "",
  accountIds: [],
  category: "",
  paymentMethods: [],
  period: "current_month",
  type: "all",
  startDate: "",
  endDate: "",
};

const PAGE_SIZE = 50;

// FIN-2: URL sync manual em vez de useSearchParams. O FinanceSection usa
// hash routing (#financeiro/extrato) e useSearchParams do react-router 7
// quebra a hash em alguns casos. Aqui lemos/escrevemos só ?query, deixando
// pathname e hash intactos.
function readFiltersFromUrl() {
  const sp = new URLSearchParams(window.location.search);
  return {
    q: sp.get("q") || "",
    accountIds: sp.getAll("accounts"),
    category: sp.get("category") || "",
    paymentMethods: sp.getAll("payments"),
    period: sp.get("period") || DEFAULTS.period,
    type: sp.get("type") || DEFAULTS.type,
    startDate: sp.get("from") || "",
    endDate: sp.get("to") || "",
  };
}

function buildSearchString(filters) {
  const sp = new URLSearchParams(window.location.search);
  ["q", "accounts", "category", "payments", "period", "type", "from", "to"].forEach(k => sp.delete(k));
  if (filters.q) sp.set("q", filters.q);
  if (filters.category) sp.set("category", filters.category);
  if (filters.period && filters.period !== DEFAULTS.period) sp.set("period", filters.period);
  if (filters.type && filters.type !== DEFAULTS.type) sp.set("type", filters.type);
  if (filters.startDate) sp.set("from", filters.startDate);
  if (filters.endDate) sp.set("to", filters.endDate);
  (filters.accountIds || []).forEach(a => sp.append("accounts", a));
  (filters.paymentMethods || []).forEach(p => sp.append("payments", p));
  return sp.toString();
}

function buildQueryParams(filters, { limit, offset }) {
  const p = new URLSearchParams();
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  p.set("type", filters.type || "all");
  if (filters.q) p.set("q", filters.q);
  if (filters.category) p.set("category", filters.category);
  if (filters.startDate && filters.endDate) {
    p.set("start_date", filters.startDate);
    p.set("end_date", filters.endDate);
  } else if (filters.period) {
    p.set("period", filters.period);
  }
  (filters.accountIds || []).forEach(a => p.append("account_ids", a));
  (filters.paymentMethods || []).forEach(pm => p.append("payment_methods", pm));
  return p;
}

export default function useFinanceExtrato() {
  // Estado de filtros derivado da URL no mount; reage a mudanças via setFilter.
  const [filters, setFilters] = useState(() => readFiltersFromUrl());

  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({ entradas: 0, saidas: 0, saldo: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isAppending, setIsAppending] = useState(false);
  const [error, setError] = useState(null);

  // q debounce — separa o input value do filter aplicado pra evitar
  // hammer no backend a cada keystroke.
  const [pendingQ, setPendingQ] = useState(filters.q);
  const debounceTimerRef = useRef(null);
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => prev.q === pendingQ ? prev : { ...prev, q: pendingQ });
    }, 300);
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
  }, [pendingQ]);

  // Sync URL ⇐ filters (reactive). Preserva pathname E hash via
  // history.replaceState manual.
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  useEffect(() => {
    const nextSearch = buildSearchString(filters);
    const currentSearch = window.location.search.replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      const url = window.location.pathname
        + (nextSearch ? `?${nextSearch}` : "")
        + window.location.hash;
      window.history.replaceState(null, "", url);
    }
  }, [filtersKey]);

  // Fetch principal — roda sempre que filtros mudam.
  const fetchPage = useCallback(async (resetOffset = true, opts = {}) => {
    const localOffset = resetOffset ? 0 : (opts.offsetOverride ?? offset);
    if (resetOffset) {
      setIsLoading(true);
    } else {
      setIsAppending(true);
    }
    setError(null);
    try {
      const qp = buildQueryParams(filters, { limit: PAGE_SIZE, offset: localOffset });
      const res = await authFetch(`${API_URL}/api/finance/transactions?${qp.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.transactions) ? data.transactions : [];
      setTransactions(prev => resetOffset ? list : [...prev, ...list]);
      setTotalCount(Number(data?.total_count || 0));
      setSummary(data?.summary || { entradas: 0, saidas: 0, saldo: 0 });
      setHasMore(Boolean(data?.has_more));
      setOffset(localOffset + list.length);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setIsLoading(false);
      setIsAppending(false);
    }
  }, [filtersKey, offset]); // eslint-disable-line react-hooks/exhaustive-deps

  // dispara refetch quando filtros mudam
  useEffect(() => {
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const setFilter = useCallback((key, value) => {
    if (key === "q") {
      setPendingQ(value);
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  }, []);

  const setManyFilters = useCallback((patch) => {
    setFilters(prev => ({ ...prev, ...patch }));
    if (Object.prototype.hasOwnProperty.call(patch, "q")) {
      setPendingQ(patch.q);
    }
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULTS });
    setPendingQ("");
  }, []);

  const fetchMore = useCallback(() => {
    if (!hasMore || isAppending || isLoading) return;
    fetchPage(false, { offsetOverride: offset });
  }, [fetchPage, hasMore, isAppending, isLoading, offset]);

  const refetch = useCallback(() => fetchPage(true), [fetchPage]);

  // Patch otimista de uma transaction (usado em edição inline de categoria)
  const patchTransaction = useCallback((id, patch) => {
    setTransactions(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const exportCsv = useCallback(async () => {
    const qp = new URLSearchParams();
    qp.set("format", "csv");
    qp.set("type", filters.type || "all");
    if (filters.q) qp.set("q", filters.q);
    if (filters.category) qp.set("category", filters.category);
    if (filters.startDate && filters.endDate) {
      qp.set("start_date", filters.startDate);
      qp.set("end_date", filters.endDate);
    } else if (filters.period) {
      qp.set("period", filters.period);
    }
    (filters.accountIds || []).forEach(a => qp.append("account_ids", a));
    (filters.paymentMethods || []).forEach(pm => qp.append("payment_methods", pm));

    const res = await authFetch(`${API_URL}/api/finance/transactions/export?${qp.toString()}`);
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m ? m[1] : `extrato_${new Date().toISOString().slice(0, 10)}.csv`;

    const a = document.createElement("a");
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { filename, size: blob.size };
  }, [filters]);

  return {
    filters: { ...filters, q: pendingQ }, // q reflete o input não debounced
    appliedFilters: filters,
    setFilter,
    setManyFilters,
    resetFilters,
    transactions,
    totalCount,
    summary,
    hasMore,
    isLoading,
    isAppending,
    error,
    fetchMore,
    refetch,
    patchTransaction,
    exportCsv,
  };
}
